import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiActivity, FiArrowRight, FiCheckCircle, FiClock, FiCompass, FiInfo, FiLock, FiMapPin, FiRefreshCw, FiShield, FiTrash2, FiZap } from "react-icons/fi";
import { useAuth } from "../context/AuthContext.tsx";
import "./TrustChecksPage.css";

type TrustKind="bot"|"location";
type Check={
  kind:TrustKind;method:string;status:string;label:string;freshness:number;observedAt:string;expiresAt:string;
  coarseRegion?:string|null;accuracyBand?:string|null;note:string;
};
type TrustResponse={checks:Check[];capabilities:{bot:boolean;location:boolean};note:string};
type TurnstileApi={
  render:(element:HTMLElement,options:{sitekey:string;theme:string;callback:(token:string)=>void;"expired-callback":()=>void;"error-callback":()=>void})=>string|number;
  reset:(widgetId:string|number)=>void;
  remove:(widgetId:string|number)=>void;
};
declare global{interface Window{turnstile?:TurnstileApi}}
const API=process.env.REACT_APP_BACKEND_URL||"/api";
const SITE_KEY=process.env.REACT_APP_TURNSTILE_SITE_KEY||"";
const HALF_LIVES:Record<TrustKind,number>={bot:12,location:24*7};
const EXPIRIES:Record<TrustKind,number>={bot:72,location:24*30};

function useTurnstile(enabled:boolean){
  const element=useRef<HTMLDivElement|null>(null);
  const widget=useRef<string|number|null>(null);
  const [token,setToken]=useState("");
  const [widgetError,setWidgetError]=useState("");
  useEffect(()=>{
    if(!enabled || !SITE_KEY || !element.current)return;
    let alive=true;
    const mount=()=>{
      if(!alive||!window.turnstile||!element.current||widget.current!==null)return;
      try{
        widget.current=window.turnstile.render(element.current,{
          sitekey:SITE_KEY,theme:"auto",
          callback:next=>{if(alive){setToken(next);setWidgetError("");}},
          "expired-callback":()=>{if(alive)setToken("");},
          "error-callback":()=>{if(alive){setToken("");setWidgetError("Challenge could not load. Try again.");}}
        });
      }catch{
        if(alive)setWidgetError("Could not initialize the security challenge.");
      }
    };
    const selector='script[data-handle-turnstile="true"]';
    let script=document.querySelector<HTMLScriptElement>(selector);
    if(!script){
      script=document.createElement("script");
      script.dataset.handleTurnstile="true";
      script.src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async=true;script.defer=true;
      document.head.appendChild(script);
    }
    script.addEventListener("load",mount);
    if(window.turnstile)mount();
    return ()=>{
      alive=false;
      script?.removeEventListener("load",mount);
      if(widget.current!==null && window.turnstile){
        try{window.turnstile.remove(widget.current);}catch{}
      }
      widget.current=null;
    };
  },[enabled]);
  const reset=()=>{
    setToken("");
    if(widget.current!==null&&window.turnstile){
      try{window.turnstile.reset(widget.current);}catch{}
    }
  };
  return {element,token,widgetError,reset};
}
function freshnessExample(kind:TrustKind,hours:number){
  if(hours>=EXPIRIES[kind])return 0;
  return Math.round(100*Math.pow(0.5,hours/HALF_LIVES[kind]));
}
function formatDate(value:string){return new Date(value).toLocaleString(undefined,{dateStyle:"medium",timeStyle:"short"});}
const TrustChecksPage:React.FC=()=>{
  const {isAuthenticated,isLoading,loginWithGoogle}=useAuth();
  const [data,setData]=useState<TrustResponse|null>(null);
  const [loading,setLoading]=useState(false);
  const [working,setWorking]=useState<TrustKind|null>(null);
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [exampleAge,setExampleAge]=useState(0);
  const [exampleKind,setExampleKind]=useState<TrustKind>("bot");
  const {element,token,widgetError,reset}=useTurnstile(isAuthenticated && Boolean(data?.capabilities.bot));

  const refresh=useCallback(async()=>{
    if(!isAuthenticated){setData(null);return;}
    setLoading(true);
    try{
      const response=await fetch(API+"/connections/trust",{credentials:"include"});
      if(!response.ok)throw new Error(response.status===500?"Trust-check database migration may be pending.":"Unable to load checks");
      setData(await response.json());
      setError("");
    }catch(e){setError(e instanceof Error?e.message:"Could not load verification checks");}
    finally{setLoading(false);}
  },[isAuthenticated]);
  useEffect(()=>{void refresh();},[refresh]);

  const submit=async(kind:TrustKind,payload:Record<string,unknown>)=>{
    setWorking(kind);setError("");setMessage("");
    try{
      const res=await fetch(API+"/connections/trust",{
        method:"POST",credentials:"include",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({kind,...payload})
      });
      const result=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(result.error||"Check could not be completed");
      setMessage(kind==="bot"
        ?"Anti-bot challenge passed. This is not proof of identity or guaranteed personhood."
        :"Coarse device location recorded with your permission. It is not independently verified.");
      await refresh();
    }catch(e){setError(e instanceof Error?e.message:"Verification failed");}
    finally{setWorking(null);if(kind==="bot")reset();}
  };
  const useLocation=()=>{
    if(!navigator.geolocation){setError("This browser does not support location permission.");return;}
    setWorking("location");setError("");setMessage("");
    navigator.geolocation.getCurrentPosition(
      position=>{
        // Exact GPS never leaves the browser. Share only a deliberately broad 2° (~200km) grid.
        const latitude=Math.floor(position.coords.latitude/2)*2;
        const longitude=Math.floor(position.coords.longitude/2)*2;
        const accuracy=position.coords.accuracy;
        if(!Number.isFinite(accuracy)||accuracy>20000){
          setWorking(null);setError("Location accuracy is too low for this optional check.");return;
        }
        const accuracyBand=accuracy<=1000?"under-1-km":accuracy<=5000?"1-to-5-km":"5-to-20-km";
        void submit("location",{coarseLatitude:latitude,coarseLongitude:longitude,accuracyBand});
      },
      e=>{setWorking(null);setError(e.code===1?"Location permission declined. No location was saved.":"Location unavailable. No location was saved.");},
      {enableHighAccuracy:false,timeout:12000,maximumAge:0}
    );
  };
  const remove=async(kind:TrustKind)=>{
    setWorking(kind);setError("");setMessage("");
    try{
      const res=await fetch(API+"/connections/trust",{
        method:"DELETE",credentials:"include",
        headers:{"Content-Type":"application/json"},body:JSON.stringify({kind})
      });
      if(!res.ok)throw new Error("Could not delete this check");
      setMessage("Your "+(kind==="bot"?"challenge":"coarse location")+" observation has been deleted.");
      await refresh();
    }catch(e){setError(e instanceof Error?e.message:"Could not delete check");}
    finally{setWorking(null);}
  };
  const get=(kind:TrustKind)=>data?.checks.find(c=>c.kind===kind);
  const bot=get("bot"),location=get("location");
  return <main className="h-page h-trust"><div className="h-container">
    <section className="h-trust-hero"><div>
      <span className="h-eyebrow"><FiShield size={16}/> HANDLE ASSURANCE / TIME-BOUND SIGNALS</span>
      <h1 className="h-display">Proof has a<br/><span className="h-gradient-text">shelf life.</span></h1>
      <p className="h-lead">Create a recent anti-bot check or voluntarily share a broad device-reported area. Every check expires, can be deleted, and stays private unless you separately approve a future use.</p>
      <div className="h-hero-actions"><span className="h-pill h-pill-info"><FiLock/> No exact GPS storage</span><span className="h-pill h-pill-purple"><FiClock/> Decay by check type</span></div>
    </div><div className="h-trust-hero-art" aria-hidden="true"><FiActivity size={42}/><span>ATTESTATION → FRESHNESS</span><strong>NO PERMANENT BADGES</strong></div></section>
    {!isAuthenticated?<section className="h-section h-panel h-panel-padding h-trust-login">
      <FiLock size={24}/><h2>Sign in to manage your checks.</h2><p>Trust checks belong to your Handle account and cannot be shared without separate consent.</p>
      <button className="h-btn h-btn-primary" type="button" onClick={loginWithGoogle} disabled={isLoading}>Sign in with Google <FiArrowRight/></button>
    </section>:<section className="h-section">
      <div className="h-section-heading"><div><span className="h-small-label">YOUR RECENT OBSERVATIONS</span><h2>Two kinds of signals. Different guarantees.</h2><p>Challenge completion is not identity verification. Browser-provided coordinates are not independent proof of residency.</p></div><button className="h-btn h-btn-secondary" type="button" onClick={()=>void refresh()} disabled={loading}><FiRefreshCw/> Refresh</button></div>
      {error&&<p role="alert" className="h-trust-error">{error}</p>}
      {message&&<p role="status" className="h-trust-success">{message}</p>}
      <div className="h-trust-check-grid">
        <article className="h-panel h-panel-padding h-trust-card">
          <div className="h-trust-card-head"><span className="h-trust-card-icon cyan"><FiShield size={26}/></span><span className={"h-pill "+(bot?.status==="challenge_passed"?"h-pill-success":"")}>{bot?(bot.status==="expired"?"Expired":"Challenge passed"):"Not checked"}</span></div>
          <h3>Anti-bot check</h3><p>A single-use Cloudflare Turnstile challenge provides a short-lived anti-abuse signal. It does not verify your name, age, or that you are human.</p>
          {bot&&<div className="h-trust-check-details"><span>Freshness <strong>{bot.freshness}%</strong></span><span>Observed <strong>{formatDate(bot.observedAt)}</strong></span><span>Expires <strong>{formatDate(bot.expiresAt)}</strong></span></div>}
          {data?.capabilities.bot&&SITE_KEY?<div ref={element} className="h-turnstile" aria-label="Cloudflare Turnstile challenge"/>:
            <div className="h-trust-setup">Bot challenge requires Cloudflare Turnstile environment keys.</div>}
          {widgetError&&<p className="h-trust-error">{widgetError}</p>}
          <div className="h-trust-actions"><button type="button" className="h-btn h-btn-primary"
            disabled={!data?.capabilities.bot||!SITE_KEY||!token||Boolean(working)}
            onClick={()=>void submit("bot",{token})}><FiCheckCircle/> {working==="bot"?"Checking…":"Save completed challenge"}</button>
            {bot&&<button type="button" className="h-btn h-btn-secondary" onClick={()=>void remove("bot")} disabled={Boolean(working)}><FiTrash2/> Delete</button>}</div>
          <small>12-hour half-life · expires after 72 hours · token valid for 5 minutes, once.</small>
        </article>
        <article className="h-panel h-panel-padding h-trust-card">
          <div className="h-trust-card-head"><span className="h-trust-card-icon violet"><FiMapPin size={26}/></span><span className={"h-pill "+(location?.status==="device_reported"?"h-pill-info":"")}>{location?(location.status==="expired"?"Expired":"Device reported"):"Not checked"}</span></div>
          <h3>Coarse location observation</h3>
          <p>Allow one location check, optionally. Your browser reduces it to a <strong>2-degree area grid</strong> before sending. No exact GPS coordinates, travel history, or location tracking.</p>
          {location&&<div className="h-trust-check-details"><span>Freshness <strong>{location.freshness}%</strong></span><span>Observed <strong>{formatDate(location.observedAt)}</strong></span><span>Expires <strong>{formatDate(location.expiresAt)}</strong></span><span>Coarse area <strong>{location.coarseRegion||"Not retained"}</strong></span></div>}
          <div className="h-trust-location-note"><FiInfo size={16}/><span>This observation is device-reported and can be spoofed; it is <strong>not independently location-verified</strong>.</span></div>
          <div className="h-trust-actions"><button type="button" className="h-btn h-btn-primary" disabled={!data?.capabilities.location||Boolean(working)} onClick={useLocation}>
            <FiCompass/> {working==="location"?"Checking…":"Share approximate location once"}</button>
            {location&&<button type="button" className="h-btn h-btn-secondary" disabled={Boolean(working)} onClick={()=>void remove("location")}><FiTrash2/> Delete</button>}</div>
          {!data?.capabilities.location&&<small>Optional location check requires an enabled server feature flag.</small>}
          <small>7-day half-life · expires after 30 days · permission always optional.</small>
        </article>
      </div>
    </section>}
    <section className="h-section h-panel h-panel-padding h-trust-example">
      <div className="h-section-heading"><div><span className="h-small-label">FRESHNESS MODEL / SIMULATION</span><h2>A check is only as current as its last observation.</h2><p>Illustrative signal freshness, not a universal trust score, identity credential, or price.</p></div><FiZap size={27}/></div>
      <div className="h-trust-example-controls">
        <label>Check type
          <select className="h-select" value={exampleKind} onChange={e=>{setExampleKind(e.target.value as TrustKind);setExampleAge(0);}}>
            <option value="bot">Bot challenge (hours)</option><option value="location">Coarse location (days)</option>
          </select>
        </label>
        <label>Time since check <strong>{exampleKind==="bot"?exampleAge+" hours":Math.round(exampleAge/24)+" days"}</strong>
          <input type="range" min={0} max={EXPIRIES[exampleKind]} step={exampleKind==="bot"?6:24} value={exampleAge}
            onChange={e=>setExampleAge(Number(e.target.value))} aria-label="Time since last check"/>
        </label>
        <div className="h-trust-example-result"><span>Illustrative freshness</span><strong>{freshnessExample(exampleKind,exampleAge)}%</strong><small>Zero at hard expiry</small></div>
      </div>
      <div className="h-divider"/>
      <div className="h-trust-boundaries"><div><FiLock/><strong>Private by default</strong><span>Checks are not offered to marketplace buyers automatically.</span></div><div><FiRefreshCw/><strong>Repeat to refresh</strong><span>Only the newest observation of each kind is retained.</span></div><div><FiShield/><strong>Honest assurance</strong><span>Neither check proves real-world identity, account ownership, or residency.</span></div></div>
      <Link to="/connections" className="h-btn h-btn-secondary">Explore source connections <FiArrowRight/></Link>
    </section>
  </div></main>;
};
export default TrustChecksPage;
